import { createAsyncThunk, createSlice, createSelector } from '@reduxjs/toolkit';
import axios from 'axios';
import api from 'API';

import { withNotificationForError } from './helper';
import { Badge, Case, Check, Completed, Info, Profile, Shareholders } from '/svg';

const ICON_COMPONENTS = { Info, Case, Profile, Badge, Shareholders, Check, finish: Completed };
const COMPANY_STEPS = [
  {
    id: 'business_complete',
    iconName: 'Info',
    title: 'pageHeader.registration.company.steps.profile',
    status: 'wait',
    path: 'profile',
    order: 1,
  },
  {
    id: 'operational_complete',
    iconName: 'Case',
    title: 'pageHeader.registration.company.steps.operational',
    status: 'wait',
    path: 'operational',
    order: 2,
  },
  {
    id: 'directors_complete',
    iconName: 'Profile',
    title: 'pageHeader.registration.company.steps.directors',
    status: 'wait',
    path: 'directors',
    order: 3,
  },
  {
    id: 'staffs_complete',
    iconName: 'Badge',
    title: 'pageHeader.registration.company.steps.representatives',
    status: 'wait',
    path: 'representatives',
    order: 4,
  },
  {
    id: 'shareholders_complete',
    iconName: 'Shareholders',
    title: 'pageHeader.registration.company.steps.shareholders',
    status: 'wait',
    path: 'shareholders',
    order: 5,
  },
  {
    id: 'finish_complete',
    iconName: 'Check',
    title: 'pageHeader.registration.company.steps.finish',
    status: 'wait',
    path: 'finish',
    order: 6,
  },
];
const INDIVIDUAL_STEPS = [
  {
    id: 'status_certification_complete',
    iconName: 'Check',
    title: 'pageHeader.registration.individual.steps.confirmation',
    status: 'wait',
    path: 'status_certification_complete',
    order: 1,
  },
];

const createStep = (stepsConfig, backendData) => {
  const lastFinished = stepsConfig.findLastIndex((step) => backendData[step.id] === 'finish');
  const isFinishedExist = lastFinished !== -1;

  return stepsConfig.map((step, index) => {
    const currentStatus = backendData[step.id];
    let newStatus = 'wait';

    if (!isFinishedExist && index === 0) {
      newStatus = 'available';
    } else if (isFinishedExist) {
      if (currentStatus === 'finish') {
        newStatus = 'finish';
      } else if (index === lastFinished + 1) {
        newStatus = 'available';
      }
    }

    return {
      ...step,
      status: newStatus,
      disabled: newStatus === 'wait',
    };
  });
};

const getPreviousStep = (stepsConfig, activeStepId) => {
  const currentStep = stepsConfig.find((step) => step.id === activeStepId);
  if (!currentStep) return null;

  const previousStep = stepsConfig
    .filter((step) => step.order < currentStep.order)
    .sort((a, b) => b.order - a.order)[0];

  return previousStep?.status === 'finish' ? previousStep : null;
};

const getNextStep = (stepsConfig, activeStepId) => {
  const currentStep = stepsConfig.find((step) => step.id === activeStepId);
  if (!currentStep) return null;

  const nextStep = stepsConfig.filter((step) => step.order > currentStep.order).sort((a, b) => a.order - b.order)[0];

  return canAccessStep(nextStep) ? nextStep : null;
};

const canAccessStep = (step) => step && ['finish', 'available'].includes(step.status);

export const fetchProgressData = createAsyncThunk(
  'progress/fetchProgressData',
  withNotificationForError(async (payload, { getState }) => {
    const userType = getState().userToken.tokenType;
    const response = await axios(api.users.profile.progress(userType).AXIOS('get'));
    const initialStepConfig = userType === 'company' ? COMPANY_STEPS : INDIVIDUAL_STEPS;

    return {
      ...response.data,
      initialStepConfig,
    };
  }),
  {
    condition: (payload, { getState }) => {
      return !!getState().userToken.tokenType;
    },
  }
);

const progressSlice = createSlice({
  name: 'progress',
  initialState: {
    loading: true,
    isLoaded: false,
    error: null,
    stepsConfig: COMPANY_STEPS,
    activeStepId: null,
    redirectPath: null,
    navigatePath: null,
  },
  reducers: {
    setActiveStep: (state, action) => {
      const pathname = action.payload;
      const currentPath = pathname.split('/').pop();

      // List of special pages that should not be in stepsConfig
      const specialPages = ['sumsub', 'jumio', 'onboarding', 'inprogress', 'docreview'];

      // If this is a special page, skip processing.
      if (specialPages.includes(currentPath)) {
        return;
      }

      const targetStep = state.stepsConfig.find((s) => s.path === currentPath);

      if (!targetStep) {
        return;
      }

      if (canAccessStep(targetStep)) {
        state.activeStepId = targetStep.id;
      } else {
        // find fists accessible or get first if no any accessible
        const firstAvailableStep =
          state.stepsConfig.filter((step) => canAccessStep(step)).pop() || state.stepsConfig[0];

        if (firstAvailableStep) {
          state.activeStepId = firstAvailableStep.id;
          state.redirectPath = firstAvailableStep.path;
        }
      }
    },
    navigatePreviousStep: (state) => {
      const previousStep = getPreviousStep(state.stepsConfig, state.activeStepId);

      if (previousStep) {
        state.activeStepId = previousStep.id;
        state.navigatePath = previousStep.path;
      }
    },
    navigateNextStep: (state) => {
      const nextStep = getNextStep(state.stepsConfig, state.activeStepId);

      if (nextStep) {
        state.activeStepId = nextStep.id;
        state.navigatePath = nextStep.path;
      }
    },
    clearRedirectPath: (state) => {
      state.redirectPath = null;
    },
    clearNavigatePath: (state) => {
      state.navigatePath = null;
    },

    optimisticStepAvailable: (state, action) => {
      const { stepId, stepPath } = action.payload;

      let step = null;
      if (stepId) {
        step = state.stepsConfig.find((s) => s.id === stepId);
      } else if (stepPath) {
        step = state.stepsConfig.find((s) => s.path === stepPath);
      }

      if (step) {
        step.status = 'available';
        step.disabled = false;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchProgressData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProgressData.fulfilled, (state, action) => {
        const { initialStepConfig, ...backendData } = action.payload;
        const updatedStepConfig = createStep(initialStepConfig, backendData);

        state.loading = false;
        state.stepsConfig = updatedStepConfig;
        state.isLoaded = true;
      })
      .addCase(fetchProgressData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      });
  },
});

const progressSelector = (state) => state.progress;

export const stepsListSelector = createSelector([progressSelector], (progress) => progress.stepsConfig);

export const activeStepSelector = createSelector([progressSelector, stepsListSelector], (progress) =>
  progress.stepsConfig.find((step) => step.id === progress.activeStepId)
);

export const stepSelector = (stepId) =>
  createSelector([progressSelector], (progress) => {
    const { stepsConfig, activeStepId } = progress;
    const step = stepsConfig.find((step) => step.id === stepId);

    if (!step) return null;

    return {
      ...step,
      disabled: step.status === 'wait',
      isFirst: step.order === 1,
      isLast: step.order === stepsConfig.length,
      Icon: ICON_COMPONENTS[step.iconName],
      status: step.id === activeStepId ? 'active' : step.status,
    };
  });

export const navigateStepSelector = createSelector(
  [progressSelector, stepsListSelector, activeStepSelector],
  (progress, stepList, activeStep) => ({
    redirectPath: progress.redirectPath,
    navigatePath: progress.navigatePath,
    canNavigatePrevStep: !!getPreviousStep(progress.stepsConfig, progress.activeStepId),
    canNavigateNextStep: !!getNextStep(progress.stepsConfig, progress.activeStepId),
    isStepsLoaded: progress.isLoaded,
    stepList,
    activeStep,
  })
);

export const {
  setActiveStep,
  clearRedirectPath,
  clearNavigatePath,
  navigatePreviousStep,
  navigateNextStep,
  optimisticStepAvailable,
} = progressSlice.actions;

export default progressSlice.reducer;
